'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { VehicleGroupForm, type VehicleGroupFormRef } from '@/components/partials/vehicle-groups'
import { type CreateVehicleGroupParams } from '@/types/vehicle-group'
import { useCreateVehicleGroup } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const vehicleGroupFormRef = useRef<VehicleGroupFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createVehicleGroup, isPending: isLoading } = useCreateVehicleGroup()

  const handleSubmit = (data: CreateVehicleGroupParams) => {
    createVehicleGroup(data, {
      onSuccess: async newVehicleGroup => {
        setIsRedirecting(true)
        queryClient.setQueryData(['vehicle-group', newVehicleGroup.id], newVehicleGroup)
        await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })
        toast.success(t('created_successfully'))
        router.push(`/products/vehicle-groups/${newVehicleGroup.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateVehicleGroupParams) => {
    createVehicleGroup(data, {
      onSuccess: async newVehicleGroup => {
        queryClient.setQueryData(['vehicle-group', newVehicleGroup.id], newVehicleGroup)
        await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })
        const detailUrl = `/products/vehicle-groups/${newVehicleGroup.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        vehicleGroupFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (vehicleGroupFormRef.current) {
        vehicleGroupFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/vehicle-groups')
  }

  return (
    <VehicleGroupForm
      key={formKey}
      ref={vehicleGroupFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
